import { CalendarDate, type DateValue } from "@internationalized/date";
import { formatInstantInTz } from "@/domains/shared/utils/zonedInstant";
import { getTranslation, toLocale } from "@mivo/i18n";

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const FORMAT_RELATIVE_TRANSLATIONS = {
  today: { "pt-BR": "Hoje", en: "Today", es: "Hoy" , de: "Heute"},
  tomorrow: { "pt-BR": "Amanhã", en: "Tomorrow", es: "Mañana" , de: "Morgen"},
  yesterday: { "pt-BR": "Ontem", en: "Yesterday", es: "Ayer" , de: "Gestern"},
  inDays: { "pt-BR": "Em {n} dias", en: "In {n} days", es: "En {n} días", de: "In {n} Tagen" },
  daysAgo: { "pt-BR": "Há {n} dias", en: "{n} days ago", es: "Hace {n} días", de: "Vor {n} Tagen" },
} as const;

function formatDisplayLocale(): string {
  return toLocale();
}

/** Parse ISO strings; date-only yyyy-MM-dd uses local calendar (no UTC day shift). */
export function parseDateInput(iso: string | Date): Date {
  if (iso instanceof Date) {
return iso;
}
  const trimmed = iso.trim();
  if (DATE_ONLY_RE.test(trimmed)) {
    const [y, m, d] = trimmed.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(trimmed);
}

export function calendarDateToIso(d: DateValue): string {
  const c = d as CalendarDate;
  if (!Number.isFinite(c.year) || !Number.isFinite(c.month) || !Number.isFinite(c.day)) {
    return "";
  }
  const y = String(c.year).padStart(4, "0");
  const m = String(c.month).padStart(2, "0");
  const day = String(c.day).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Parse `yyyy-MM-dd` into a CalendarDate.
 * Returns null for empty/malformed input — CalendarDate silently accepts NaN and
 * later blows up as `RangeError: Invalid time value` inside calendar day/month maps.
 */
export function tryIsoToCalendarDate(iso: string | null | undefined): CalendarDate | null {
  const trimmed = iso?.trim();
  if (!trimmed || !DATE_ONLY_RE.test(trimmed)) {
    return null;
  }
  const [y, m, d] = trimmed.split("-").map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return null;
  }
  if (m < 1 || m > 12 || d < 1 || d > 31) {
    return null;
  }
  try {
    return new CalendarDate(y, m, d);
  } catch {
    return null;
  }
}

export function isoToCalendarDate(iso: string): CalendarDate {
  const parsed = tryIsoToCalendarDate(iso);
  if (!parsed) {
    throw new RangeError(`Invalid date-only ISO: ${iso}`);
  }
  return parsed;
}

export type DatePickerGranularity = "day" | "month" | "year";

/** Display label for DatePickerField trigger by granularity. */
export function formatDatePickerLabel(
  iso: string | undefined | null,
  granularity: DatePickerGranularity = "day",
): string {
  if (!iso?.trim()) {
return "";
}
  const trimmed = iso.trim();
  if (!DATE_ONLY_RE.test(trimmed)) {
return formatDate(trimmed);
}

  const [y, m] = trimmed.split("-").map(Number);
  switch (granularity) {
    case "month":
      return `${String(m).padStart(2, "0")}/${y}`;
    case "year":
      return String(y);
    default:
      return formatDate(trimmed);
  }
}

/** Normalize stored ISO to first day of month/year for month/year pickers. */
export function normalizeDatePickerIso(iso: string, granularity: DatePickerGranularity): string {
  const d = tryIsoToCalendarDate(iso);
  if (!d) {
    return "";
  }
  switch (granularity) {
    case "month":
      return calendarDateToIso(new CalendarDate(d.year, d.month, 1));
    case "year":
      return calendarDateToIso(new CalendarDate(d.year, 1, 1));
    default:
      return calendarDateToIso(d);
  }
}

export function formatDate(
  iso: string | Date | undefined | null,
  opts: Intl.DateTimeFormatOptions = {},
  timeZoneId?: string,
) {
  if (!iso) {
return "—";
}
  const d = typeof iso === "string" ? parseDateInput(iso) : iso;
  if (Number.isNaN(d.getTime())) {
return "—";
}
  if (timeZoneId) {
    return formatInstantInTz(d, timeZoneId, { day: "2-digit", month: "2-digit", year: "numeric", ...opts });
  }
  return d.toLocaleDateString(formatDisplayLocale(), { day: "2-digit", month: "2-digit", year: "numeric", ...opts });
}

export function formatDateTime(iso: string | Date | undefined | null, timeZoneId?: string) {
  if (!iso) {
return "—";
}
  const d = typeof iso === "string" ? parseDateInput(iso) : iso;
  if (Number.isNaN(d.getTime())) {
return "—";
}
  if (timeZoneId) {
    return formatInstantInTz(d, timeZoneId, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleString(formatDisplayLocale(), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatTime(iso: string | Date | undefined | null, timeZoneId?: string) {
  if (!iso) {
return "—";
}
  const d = typeof iso === "string" ? parseDateInput(iso) : iso;
  if (Number.isNaN(d.getTime())) {
return "—";
}
  if (timeZoneId) {
    return formatInstantInTz(d, timeZoneId, { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleTimeString(formatDisplayLocale(), { hour: "2-digit", minute: "2-digit" });
}

export function relativeDay(iso: string | Date | undefined | null, timeZoneId?: string) {
  if (!iso) {
return "—";
}
  const d = typeof iso === "string" ? parseDateInput(iso) : iso;
  if (Number.isNaN(d.getTime())) {
return "—";
}
  if (timeZoneId) {
    const dateLabel = formatInstantInTz(d, timeZoneId, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const todayLabel = formatInstantInTz(new Date(), timeZoneId, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    if (dateLabel === todayLabel) {
return getTranslation(FORMAT_RELATIVE_TRANSLATIONS.today);
}
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowLabel = formatInstantInTz(tomorrow, timeZoneId, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    if (dateLabel === tomorrowLabel) {
return getTranslation(FORMAT_RELATIVE_TRANSLATIONS.tomorrow);
}
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayLabel = formatInstantInTz(yesterday, timeZoneId, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    if (dateLabel === yesterdayLabel) {
return getTranslation(FORMAT_RELATIVE_TRANSLATIONS.yesterday);
}
    return formatDate(iso, {}, timeZoneId);
  }
  const now = new Date();
  const diffDays = Math.round((d.setHours(0, 0, 0, 0) - now.setHours(0, 0, 0, 0)) / 86400000);
  if (diffDays === 0) {
return getTranslation(FORMAT_RELATIVE_TRANSLATIONS.today);
}
  if (diffDays === 1) {
return getTranslation(FORMAT_RELATIVE_TRANSLATIONS.tomorrow);
}
  if (diffDays === -1) {
return getTranslation(FORMAT_RELATIVE_TRANSLATIONS.yesterday);
}
  if (diffDays > 1 && diffDays < 7) {
    return getTranslation(FORMAT_RELATIVE_TRANSLATIONS.inDays).replace("{n}", String(diffDays));
  }
  if (diffDays < 0 && diffDays > -7) {
    return getTranslation(FORMAT_RELATIVE_TRANSLATIONS.daysAgo).replace("{n}", String(Math.abs(diffDays)));
  }
  return formatDate(iso);
}

export function initials(name: string | undefined | null) {
  if (!name) {
return "?";
}
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]!.toUpperCase())
    .join("");
}
